// src/utils/patientReportDownload.js
//
// The chat's "Download PDF" card: the SAME patient-view report as the report page's Download PDF button. It loads the same data
// (loadPatientReport) and builds the document the same way TriageReportModal does, so nothing differs between the two downloads.
import { loadPatientReport } from './patientReportData';
import {
  buildPrintHtml, buildMarkers, parseJson, parseList, parseModel3Evidence, sevLevel,
} from '../components/healthrecords/TriageReportModal';

const LOAD_TIMEOUT_MS = 15000;

const buildHtml = (patient, { result, record, history, journey }, showOptimal) => {
  const markers = buildMarkers(parseJson(result.claudeRawResponse));
  const m3Full = parseModel3Evidence(result.deterministicFeatureFlags, result.claudeRawResponse);
  const notOptimal = (m) => !m?.severity || sevLevel(m.severity) !== 'low';
  const m3 = showOptimal ? m3Full : {
    ...m3Full,
    scored: (m3Full.scored || []).filter(notOptimal),
    unscored: (m3Full.unscored || []).filter(notOptimal),
  };
  const seen = new Set();
  const missing = [...parseList(result.claudeMissingMarkers), ...parseList(result.rfMissingKeys)].filter(m => {
    const key = m.toLowerCase().trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const reportId = `PRV-${String(patient?.id || '').slice(0, 8).toUpperCase()}-`
    + `${new Date(result.createdAt || Date.now()).toISOString().slice(0, 10).replace(/-/g, '')}`;
  return buildPrintHtml(reportId, patient, result, markers, missing,
    record, history, m3, true, showOptimal, journey);
};

/**
 * Opens the print window inside the click (so pop-up blockers allow it), then writes the report once its data has loaded.
 * @returns { success: true } or { success: false, reason: 'popup_blocked' | 'no_data' }
 */
export function downloadPatientReport(patient, triageResult, { showOptimal = false } = {}) {
  if (!triageResult || !patient?.id) return { success: false, reason: 'no_data' };
  const win = window.open('', '_blank', 'width=1000,height=700');
  if (!win) return { success: false, reason: 'popup_blocked' };
  win.document.write('<!doctype html><title>Previu report</title><p style="font:14px sans-serif;padding:24px">Preparing your report…</p>');

  const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), LOAD_TIMEOUT_MS));
  Promise.race([loadPatientReport(patient.id, triageResult.id), timeout])
    // If the data cannot be loaded, fall back to the result the chat already had rather than losing the download.
    .catch(() => ({ result: triageResult, record: { drawDate: triageResult.drawDate }, history: [], journey: null }))
    .then((data) => {
      win.document.open();
      win.document.write(buildHtml(patient, data, showOptimal));
      win.document.close();
      win.onload = () => { setTimeout(() => { win.focus(); win.print(); }, 800); };
    });
  return { success: true };
}
